/**
 * Sector operations — Evidence export, record-class retention, and legal hold shared contracts.
 * R39 A11.a (plan docs/plans/sector-operations.md §2 SO-P73, SO-P9, SO-P12, SO-P13, SO-P41, SO-P42, SO-P217).
 */

import { OPS_SECTOR_KEYS, type OpsSector } from './places.js'

export const SO_P9_RESTRICTED_KINDS = [
  'INCIDENT',
  'COMPLAINT',
  'STAFF_HEALTH',
  'PATIENT_DIRECTORY',
] as const

export type EvidenceSourceCollection =
  | 'ops_records'
  | 'ops_requests'
  | 'ops_compliance_items'
  | 'ops_assets'
  | 'ops_approvals'
  | 'ops_attendance'
  | 'ops_rosters'
  | 'ops_roster_versions'
  | 'ops_wage_sheets'
  | 'ops_room_states'
  | 'con_issues'
  | 'con_measurements'
  | 'ops_bmw_days'
  | 'ops_bmw_pickups'
  | 'ops_cli_narc_moves'
  | 'ops_cli_narc_counts'
  | 'ops_cli_sample_bags'
  | 'ops_cli_sample_handovers'
  | 'ops_cli_job_orders'
  | 'ops_cli_dialyzers'
  | 'ops_cli_tele_sessions'
  | 'ops_cli_ivf_witness'

export interface EvidenceSourceDef {
  collection: EvidenceSourceCollection
  sectorField: string
  kindKeyBuilder: (doc: Record<string, unknown>) => string
  placeField?: string
  attachmentFields: readonly string[]
  fieldAllowlist: readonly string[]
  isRestricted: (doc: Record<string, unknown>) => boolean
  retentionClass: string
  anchorField: string
  subcollections?: readonly string[]
  isFinished?: (doc: Record<string, unknown>, clock?: Date) => boolean
  resolveAnchor?: (doc: Record<string, unknown>) => Date | null
}

export function parseTimestampOrDate(val: unknown): Date | null {
  if (!val) return null
  if (val instanceof Date) return val
  if (typeof val === 'object' && val !== null) {
    const record = val as Record<string, unknown>
    if ('toDate' in record && typeof record.toDate === 'function') {
      return (record.toDate as () => Date)()
    }
    const s =
      typeof record._seconds === 'number'
        ? record._seconds
        : typeof record.seconds === 'number'
          ? record.seconds
          : undefined
    if (typeof s === 'number') {
      return new Date(s * 1000)
    }
  }
  if (typeof val === 'string' || typeof val === 'number') {
    const d = new Date(val)
    if (!Number.isNaN(d.getTime())) return d
  }
  return null
}

export const OPS_EVENTS_ALLOWLIST: Record<string, readonly string[]> = {
  ops_requests: ['id', 'requestId', 'type', 'byUid', 'byName', 'at', 'toState', 'reason'],
  ops_approvals: ['id', 'approvalId', 'type', 'at', 'byUid', 'byName', 'toStatus', 'reason', 'note'],
  ops_room_states: [
    'id',
    'workspaceId',
    'placeId',
    'action',
    'actorUid',
    'actorName',
    'from',
    'to',
    'source',
    'at',
    'metadata',
  ],
}

export function canCallerReadRestrictedEvidenceKind(
  source: EvidenceSourceCollection,
  kindKey: string,
  caller: {
    isOwner?: boolean
    isAdmin?: boolean
    role?: string
    sectorRoles?: string[]
  },
  kindDef?: { viewerRoleKeys?: readonly string[] | string[] },
): boolean {
  if (caller.isOwner || caller.isAdmin) return true
  const sectorRoles = caller.sectorRoles || []
  if (source === 'ops_requests') {
    if (kindDef?.viewerRoleKeys && kindDef.viewerRoleKeys.length > 0) {
      return kindDef.viewerRoleKeys.some((k) => sectorRoles.includes(k))
    }
    return sectorRoles.includes('INCIDENT_HANDLER')
  }
  if (source === 'ops_records') {
    const rawKind = (kindKey.startsWith('RECORD:') ? kindKey.slice('RECORD:'.length) : kindKey).toUpperCase()
    if (rawKind.includes('INCIDENT')) {
      return sectorRoles.includes('INCIDENT_HANDLER')
    }
    if (rawKind.includes('COMPLAINT')) {
      return sectorRoles.includes('COMPLIANCE_MANAGER') || sectorRoles.includes('INCIDENT_HANDLER')
    }
    if (rawKind.includes('STAFF_HEALTH')) {
      return sectorRoles.includes('ROSTER_MANAGER')
    }
    if (rawKind.includes('PATIENT_DIRECTORY')) {
      return sectorRoles.includes('RECORD_APPROVER')
    }
    return (
      sectorRoles.includes('RECORD_REVIEWER') ||
      sectorRoles.includes('RECORD_APPROVER') ||
      sectorRoles.includes('INCIDENT_HANDLER')
    )
  }
  return false
}

export function resolveItemRetentionClass(
  source: EvidenceSourceCollection,
  data: Record<string, unknown>,
  templateRetentionClass?: string | null,
  templateStarterKey?: string | null,
): string {
  if (source === 'ops_records') {
    if (templateRetentionClass && typeof templateRetentionClass === 'string' && templateRetentionClass.trim()) {
      return templateRetentionClass.trim()
    }
    const starterKey = (
      (templateStarterKey && typeof templateStarterKey === 'string' && templateStarterKey.trim()) ||
      (typeof data.starterKey === 'string' && data.starterKey.trim()) ||
      (typeof data.templateStarterKey === 'string' && data.templateStarterKey.trim()) ||
      (typeof (data.templateSnapshot as Record<string, unknown> | undefined)?.starterKey === 'string' &&
        ((data.templateSnapshot as Record<string, unknown>).starterKey as string).trim()) ||
      ''
    )
    if (starterKey === 'CLI_PCPNDT_FORM_F') return 'PCPNDT_FORM_F'
    if (starterKey === 'CLI_BLOOD_PRBC' || starterKey === 'CLI_PLATELETS' || starterKey === 'CLI_FFP') return 'BLOOD_BANK'
    if (starterKey === 'CLI_LEAD_APRON_CHECK') return 'AERB_DOSE'
  }
  // Narcotic docs map by schedule (H1 / X / NDPS) (R39 M16.a, SO-P274)
  const rawSchedule = data.schedule || data.narcSchedule
  if (typeof rawSchedule === 'string') {
    const s = rawSchedule.trim().toUpperCase()
    if (s === 'H1') return 'SCHEDULE_H1'
    if (s === 'X') return 'SCHEDULE_X'
    if (s === 'NDPS') return 'NDPS_REGISTER'
  }
  if (source === 'ops_requests') {
    const kindSnapshot = data.kindSnapshot as { restricted?: boolean; viewerRoleKeys?: readonly string[] | string[] } | undefined
    if (kindSnapshot?.restricted && Array.isArray(kindSnapshot.viewerRoleKeys)) {
      if (kindSnapshot.viewerRoleKeys.includes('INCIDENT_HANDLER')) return 'INCIDENT'
      if (kindSnapshot.viewerRoleKeys.includes('QUALITY_RELEASER')) return 'QUALITY_HOLD'
    }
    const kindKey = String(data.kindKey || data.kind || '').toUpperCase()
    if (kindKey.includes('INCIDENT')) return 'INCIDENT'
    if (kindKey.includes('QUALITY_HOLD')) return 'QUALITY_HOLD'
  }
  if (source === 'ops_approvals') {
    const kindKey = String(data.kindKey || '').toUpperCase()
    const subjectType = String(data.subjectType || '').toUpperCase()
    if (kindKey.includes('CR') || subjectType === 'CR') return 'CR_DECISION'
  }
  if (typeof data.retentionClass === 'string' && data.retentionClass.trim()) {
    return data.retentionClass.trim()
  }
  return OPS_EVIDENCE_SOURCES[source]?.retentionClass || 'OPERATIONAL_REGISTER'
}

export function isRestrictedRecordDoc(doc: Record<string, unknown>): boolean {
  return Boolean(
    doc.safetyTagged === true ||
      doc.patientLinked === true ||
      (typeof doc.kind === 'string' &&
        SO_P9_RESTRICTED_KINDS.some((k) => (doc.kind as string).toUpperCase().includes(k))),
  )
}

export function isRestrictedRequestDoc(doc: Record<string, unknown>): boolean {
  return Boolean(doc.restricted === true || doc.patientLinked === true)
}

export const OPS_EVIDENCE_SOURCES: Record<EvidenceSourceCollection, EvidenceSourceDef> = {
  ops_records: {
    collection: 'ops_records',
    sectorField: 'sector',
    kindKeyBuilder: (doc) => `RECORD:${String(doc.kind || doc.templateKind || 'RECORD')}`,
    placeField: 'placeId',
    attachmentFields: ['photos', 'attachments'],
    fieldAllowlist: [
      'id',
      'workspaceId',
      'sector',
      'templateId',
      'templateVersion',
      'templateName',
      'templateKind',
      'kind',
      'placeId',
      'placeName',
      'shiftId',
      'submittedAt',
      'submittedBy',
      'submittedByName',
      'lockedAt',
      'lockedBy',
      'reviewedAt',
      'reviewedBy',
      'approvedAt',
      'approvedBy',
      'state',
      'data',
      'values',
      'correctionCount',
      'safetyTagged',
      'patientLinked',
      'notes',
      'retention',
      'createdAt',
      'updatedAt',
      'createdBy',
      'updatedBy',
    ],
    isRestricted: isRestrictedRecordDoc,
    retentionClass: 'OPERATIONAL_REGISTER',
    anchorField: 'lockedAt',
    subcollections: ['corrections'],
    // records: locked or approved
    isFinished: (doc) =>
      doc.state === 'LOCKED' || doc.state === 'APPROVED' || Boolean(doc.lockedAt) || Boolean(doc.approvedAt),
    resolveAnchor: (doc) => {
      if (doc.approvedAt) return parseTimestampOrDate(doc.approvedAt)
      if (doc.lockedAt) return parseTimestampOrDate(doc.lockedAt)
      return null
    },
  },

  ops_requests: {
    collection: 'ops_requests',
    sectorField: 'sector',
    kindKeyBuilder: (doc) => `REQUEST:${String(doc.kindKey || doc.kind || 'REQUEST')}`,
    placeField: 'placeId',
    attachmentFields: ['photos', 'attachments'],
    fieldAllowlist: [
      'id',
      'workspaceId',
      'sector',
      'kindKey',
      'title',
      'description',
      'placeId',
      'placeName',
      'assignedTo',
      'assignedToName',
      'dueAt',
      'escalatedAt',
      'state',
      'priority',
      'closedAt',
      'closedBy',
      'verifiedAt',
      'verifiedBy',
      'notes',
      'raisedBy',
      'raisedByUid',
      'raisedByName',
      'assigneeUid',
      'doneByUid',
      'verifiedByUid',
      'category',
      'whatNearlyHappened',
      'immediateAction',
      'holdItem',
      'quantity',
      'unit',
      'reason',
      'downtimeReason',
      'downtimeNote',
      'fields',
      'restricted',
      'patientLinked',
      'retention',
      'createdAt',
      'updatedAt',
      'createdBy',
      'updatedBy',
    ],
    isRestricted: isRestrictedRequestDoc,
    retentionClass: 'OPERATIONAL_REGISTER',
    anchorField: 'closedAt',
    subcollections: ['events'],
    // requests: VERIFIED, DONE with no verify needed, or CANCELLED
    // SO-P73 F1: verifyRequired lives on kindSnapshot (requests.ts:1767), not on doc directly
    isFinished: (doc) =>
      doc.state === 'VERIFIED' ||
      (doc.state === 'DONE' && !(doc.kindSnapshot as { verifyRequired?: boolean } | undefined)?.verifyRequired) ||
      doc.state === 'CANCELLED',
    resolveAnchor: (doc) => {
      if (doc.state === 'VERIFIED' && doc.verifiedAt) return parseTimestampOrDate(doc.verifiedAt)
      // verifyRequired lives on kindSnapshot (requests.ts:1767)
      if (
        doc.state === 'DONE' &&
        !(doc.kindSnapshot as { verifyRequired?: boolean } | undefined)?.verifyRequired &&
        doc.doneAt
      ) {
        return parseTimestampOrDate(doc.doneAt)
      }
      // Cancel writes no cancelledAt (requests.ts:2217-2221 sets state, nextEscalationAt, updatedAt only); CANCELLED is terminal
      if (doc.state === 'CANCELLED') return parseTimestampOrDate(doc.updatedAt)
      return null
    },
  },

  ops_compliance_items: {
    collection: 'ops_compliance_items',
    sectorField: 'sector',
    kindKeyBuilder: (doc) => `COMPLIANCE:${String(doc.kindKey || doc.category || doc.type || 'ITEM')}`,
    placeField: 'placeId',
    attachmentFields: ['documentUrl', 'certificateUrl', 'attachments'],
    fieldAllowlist: [
      'id',
      'workspaceId',
      'sector',
      'title',
      'category',
      'type',
      'placeId',
      'ownerId',
      'backupOwnerId',
      'dueAt',
      'expiryDate',
      'issuedDate',
      'renewalWindowDays',
      'status',
      'verifiedAt',
      'verifiedBy',
      'licenceNumber',
      'issuingAuthority',
      'notes',
      'retention',
      'createdAt',
      'updatedAt',
      'createdBy',
      'updatedBy',
    ],
    isRestricted: () => false,
    retentionClass: 'OPERATIONAL_REGISTER',
    anchorField: 'updatedAt',
    // compliance: archived
    isFinished: (doc) => doc.status === 'ARCHIVED' || doc.archived === true,
    resolveAnchor: (doc) => {
      if (doc.status === 'ARCHIVED' || doc.archived === true) {
        return parseTimestampOrDate(doc.archivedAt || doc.updatedAt)
      }
      return null
    },
  },

  ops_assets: {
    collection: 'ops_assets',
    sectorField: 'sector',
    kindKeyBuilder: (doc) => `ASSET:${String(doc.kindKey || doc.category || doc.type || 'ASSET')}`,
    placeField: 'placeId',
    attachmentFields: ['attachments', 'manualUrl', 'photos'],
    fieldAllowlist: [
      'id',
      'workspaceId',
      'sector',
      'code',
      'name',
      'category',
      'type',
      'placeId',
      'model',
      'serialNumber',
      'manufacturer',
      'commissionedAt',
      'decommissionedAt',
      'status',
      'releasedToUse',
      'releasedAt',
      'releasedBy',
      'lastServiceAt',
      'nextServiceDueAt',
      'calibrationDueAt',
      'amcExpiryAt',
      'notes',
      'retention',
      'createdAt',
      'updatedAt',
      'createdBy',
      'updatedBy',
    ],
    isRestricted: () => false,
    retentionClass: 'OPERATIONAL_REGISTER',
    anchorField: 'updatedAt',
    // assets: RETIRED
    isFinished: (doc) => doc.status === 'RETIRED' || doc.retired === true,
    resolveAnchor: (doc) => {
      if (doc.status === 'RETIRED' || doc.retired === true) {
        return parseTimestampOrDate(doc.retiredAt || doc.decommissionedAt || doc.updatedAt)
      }
      return null
    },
  },

  ops_approvals: {
    collection: 'ops_approvals',
    sectorField: 'sector',
    kindKeyBuilder: (doc) => String(doc.kindKey || (doc.subjectType ? `${String(doc.subjectType)}:${String(doc.subjectId)}` : 'APPROVAL')),
    placeField: 'placeId',
    attachmentFields: [],
    fieldAllowlist: [
      'id',
      'workspaceId',
      'sector',
      'subjectType',
      'subjectId',
      'kindKey',
      'title',
      'mode',
      'message',
      'expiresAt',
      'status',
      'approvers',
      'decidedAt',
      'decidedBy',
      'version',
      'snapshot',
      'snapshotHash',
      'retention',
      'createdAt',
      'updatedAt',
      'createdBy',
      'updatedBy',
    ],
    isRestricted: () => false,
    retentionClass: 'CLIENT_APPROVAL',
    anchorField: 'decidedAt',
    subcollections: ['events'],
    // approvals: decided, expired or revoked
    isFinished: (doc) => ['APPROVED', 'REJECTED', 'EXPIRED', 'REVOKED'].includes(doc.status as string),
    resolveAnchor: (doc) => {
      if (doc.status === 'EXPIRED') return parseTimestampOrDate(doc.expiresAt || doc.updatedAt)
      if (['APPROVED', 'REJECTED', 'REVOKED'].includes(doc.status as string)) {
        return parseTimestampOrDate(doc.decidedAt || doc.updatedAt)
      }
      return null
    },
  },

  ops_attendance: {
    collection: 'ops_attendance',
    sectorField: 'sector',
    kindKeyBuilder: () => 'ATTENDANCE',
    placeField: 'placeId',
    attachmentFields: ['photoUrl', 'photoPath'],
    fieldAllowlist: [
      'id',
      'workspaceId',
      'sector',
      'personRefKey',
      'personName',
      'placeId',
      'shiftId',
      'dayKey',
      'status',
      'actualIn',
      'actualOut',
      'markedAt',
      'markedBy',
      'verifiedAt',
      'verifiedBy',
      'notes',
      'correctionReason',
      'retention',
      'createdAt',
      'updatedAt',
      'createdBy',
      'updatedBy',
    ],
    isRestricted: () => false,
    retentionClass: 'OPERATIONAL_REGISTER',
    anchorField: 'markedAt',
    // attendance: marked
    isFinished: (doc) => doc.status !== 'DRAFT' && Boolean(doc.markedAt),
    resolveAnchor: (doc) => {
      if (doc.status !== 'DRAFT' && doc.markedAt) {
        return parseTimestampOrDate(doc.markedAt)
      }
      return null
    },
  },

  ops_rosters: {
    collection: 'ops_rosters',
    sectorField: 'sector',
    kindKeyBuilder: () => 'ROSTER',
    placeField: 'placeId',
    attachmentFields: [],
    fieldAllowlist: [
      'id',
      'workspaceId',
      'sector',
      'placeId',
      'periodStart',
      'periodEnd',
      'status',
      'publishedAt',
      'publishedBy',
      'version',
      'entries',
      'shifts',
      'notes',
      'retention',
      'createdAt',
      'updatedAt',
      'createdBy',
      'updatedBy',
    ],
    isRestricted: () => false,
    retentionClass: 'OPERATIONAL_REGISTER',
    anchorField: 'publishedAt',
    // rosters: published and past
    isFinished: (doc, clock = new Date()) => {
      const isPublished = doc.status === 'PUBLISHED' || Number(doc.publishedVersion) > 0
      if (!isPublished) return false
      const dayKey = clock.toISOString().slice(0, 10)
      return typeof doc.periodEnd === 'string' && doc.periodEnd < dayKey
    },
    resolveAnchor: (doc) => {
      if (doc.status === 'PUBLISHED' || Number(doc.publishedVersion) > 0) {
        return parseTimestampOrDate(doc.publishedAt || doc.updatedAt)
      }
      return null
    },
  },

  ops_roster_versions: {
    collection: 'ops_roster_versions',
    sectorField: 'sector',
    kindKeyBuilder: () => 'ROSTER_VERSION',
    placeField: 'placeId',
    attachmentFields: [],
    fieldAllowlist: [
      'id',
      'workspaceId',
      'rosterId',
      'version',
      'placeId',
      'sector',
      'periodStart',
      'periodEnd',
      'legalOverride',
      'warnings',
      'publishedBy',
      'publishedAt',
      'createdAt',
      'createdBy',
    ],
    isRestricted: () => false,
    retentionClass: 'OPERATIONAL_REGISTER',
    anchorField: 'publishedAt',
    isFinished: (doc) => Boolean(doc.legalOverride),
    resolveAnchor: (doc) => {
      if (doc.publishedAt) return parseTimestampOrDate(doc.publishedAt)
      if (doc.createdAt) return parseTimestampOrDate(doc.createdAt)
      return null
    },
  },

  ops_wage_sheets: {
    collection: 'ops_wage_sheets',
    sectorField: 'sector',
    kindKeyBuilder: () => 'WAGE_SHEET',
    placeField: 'placeId',
    attachmentFields: [],
    fieldAllowlist: [
      'id',
      'workspaceId',
      'sector',
      'periodStart',
      'periodEnd',
      'partyId',
      'status',
      'lockedAt',
      'lockedBy',
      'attendanceFingerprint',
      'rowsCount',
      'grossTotalMinor',
      'currency',
      'notes',
      'retention',
      'createdAt',
      'updatedAt',
      'createdBy',
      'updatedBy',
    ],
    isRestricted: () => false,
    retentionClass: 'WAGE_SHEET',
    anchorField: 'lockedAt',
    // wage sheets: locked
    isFinished: (doc) => doc.locked === true || doc.status === 'LOCKED',
    resolveAnchor: (doc) => {
      if (doc.locked === true || doc.status === 'LOCKED') {
        return parseTimestampOrDate(doc.lockedAt || doc.updatedAt)
      }
      return null
    },
  },

  ops_room_states: {
    collection: 'ops_room_states',
    sectorField: 'sector',
    kindKeyBuilder: () => 'ROOM_STATE',
    placeField: 'placeId',
    attachmentFields: [],
    fieldAllowlist: [
      'id',
      'workspaceId',
      'placeId',
      'propertyId',
      'blockId',
      'sector',
      'occupancy',
      'dueOutAt',
      'hk',
      'service',
      'serviceReason',
      'workOrderRequestId',
      'dnd',
      'vip',
      'cleanRecordId',
      'attendantUid',
      'startedAt',
      'cleanedAt',
      'inspectedBy',
      'failedItems',
      'pausedAt',
      'pausedMs',
      'pauseReason',
      'startGeo',
      'version',
      'retention',
      'createdAt',
      'updatedAt',
      'createdBy',
      'updatedBy',
    ],
    isRestricted: () => false,
    retentionClass: 'OPERATIONAL_REGISTER',
    anchorField: 'updatedAt',
    subcollections: ['events'],
    // room states: finished unless hk is CLEANING (mid-clean is still live)
    isFinished: (doc) => doc.hk !== 'CLEANING',
    resolveAnchor: (doc) => {
      if (doc.hk !== 'CLEANING' && doc.updatedAt) {
        return parseTimestampOrDate(doc.updatedAt)
      }
      return null
    },
  },

  con_issues: {
    collection: 'con_issues',
    sectorField: 'sector',
    kindKeyBuilder: (doc) => `ISSUE:${String(doc.kind || 'ISSUE')}`,
    placeField: 'placeId',
    attachmentFields: ['photoPaths'],
    fieldAllowlist: [
      'id',
      'workspaceId',
      'projectId',
      'siteId',
      'sector',
      'kind',
      'registerNo',
      'title',
      'description',
      'firstNoticedAt',
      'firstNoticedDay',
      'placeId',
      'responsibleParty',
      'drawingRef',
      'contractNoticeRef',
      'responseDueAt',
      'impact',
      'photoPaths',
      'options',
      'state',
      'requestId',
      'currentDecisionVersion',
      'approvalId',
      'approvalState',
      'requestCloseError',
      'decidedAt',
      'decidedBy',
      'withdrawnAt',
      'withdrawnBy',
      'withdrawnReason',
      'closedAt',
      'closedBy',
      'retention',
      'createdAt',
      'updatedAt',
      'createdBy',
    ],
    isRestricted: () => false,
    retentionClass: 'CR_DECISION',
    anchorField: 'decidedAt',
    subcollections: ['decisions', 'responses', 'edits'],
    isFinished: (doc) =>
      doc.state === 'DECIDED' || doc.state === 'CLOSED' || doc.state === 'WITHDRAWN',
    resolveAnchor: (doc) => {
      if (doc.closedAt) return parseTimestampOrDate(doc.closedAt)
      if (doc.decidedAt) return parseTimestampOrDate(doc.decidedAt)
      if (doc.withdrawnAt) return parseTimestampOrDate(doc.withdrawnAt)
      if (doc.updatedAt) return parseTimestampOrDate(doc.updatedAt)
      return null
    },
  },

  con_measurements: {
    collection: 'con_measurements',
    sectorField: 'sector',
    kindKeyBuilder: (doc) => `MEASURE:${String(doc.partyId || 'DEFAULT')}`,
    placeField: 'placeId',
    attachmentFields: ['photoPaths'],
    fieldAllowlist: [
      'id',
      'workspaceId',
      'projectId',
      'siteId',
      'sector',
      'partyId',
      'partyName',
      'placeId',
      'workOrderRef',
      'itemRef',
      'itemDescription',
      'unit',
      'measuredDay',
      'periodLabel',
      'measuredQty',
      'acceptedQty',
      'cumulativeAcceptedQty',
      'photoPaths',
      'inspectionRefs',
      'sourceLinks',
      'state',
      'submittedBy',
      'submittedAt',
      'decidedBy',
      'decidedAt',
      'disputeReason',
      'selfAcceptReason',
      'revision',
      'kindKey',
      'createdAt',
      'updatedAt',
      'createdBy',
    ],
    isRestricted: () => false,
    retentionClass: 'MEASUREMENT_PACK',
    anchorField: 'decidedAt',
    subcollections: ['revisions', 'corrections'],
    isFinished: (doc) =>
      doc.state === 'ACCEPTED' || doc.state === 'DISPUTED',
    resolveAnchor: (doc) => {
      if (doc.decidedAt) return parseTimestampOrDate(doc.decidedAt)
      if (doc.submittedAt) return parseTimestampOrDate(doc.submittedAt)
      if (doc.updatedAt) return parseTimestampOrDate(doc.updatedAt)
      return null
    },
  },

  ops_bmw_days: {
    collection: 'ops_bmw_days',
    sectorField: 'sector',
    kindKeyBuilder: () => 'BMW_DAY',
    placeField: 'placeId',
    attachmentFields: [],
    fieldAllowlist: [
      'id',
      'workspaceId',
      'placeId',
      'dayKey',
      'weights',
      'bagCounts',
      'state',
      'enteredBy',
      'enteredAt',
      'lockedAt',
      'lockedBy',
      'pickupId',
      'corrections',
      'createdAt',
      'updatedAt',
    ],
    isRestricted: () => false,
    retentionClass: 'BMW_REGISTER',
    anchorField: 'lockedAt',
    isFinished: (doc) => doc.state === 'SUBMITTED' || Boolean(doc.lockedAt),
    resolveAnchor: (doc) => {
      if (doc.lockedAt) return parseTimestampOrDate(doc.lockedAt)
      if (doc.updatedAt) return parseTimestampOrDate(doc.updatedAt)
      return null
    },
  },

  ops_bmw_pickups: {
    collection: 'ops_bmw_pickups',
    sectorField: 'sector',
    kindKeyBuilder: () => 'BMW_PICKUP',
    placeField: 'placeId',
    attachmentFields: [],
    fieldAllowlist: [
      'id',
      'workspaceId',
      'placeId',
      'pickedAt',
      'manifestRef',
      'vehicleRef',
      'bagCounts',
      'coveredDayKeys',
      'recordedBy',
      'recordedAt',
      'createdAt',
      'updatedAt',
    ],
    isRestricted: () => false,
    retentionClass: 'BMW_REGISTER',
    anchorField: 'pickedAt',
    isFinished: (doc) => Boolean(doc.pickedAt),
    resolveAnchor: (doc) => {
      if (doc.pickedAt) return parseTimestampOrDate(doc.pickedAt)
      return null
    },
  },

  ops_cli_narc_moves: {
    collection: 'ops_cli_narc_moves',
    sectorField: 'sector',
    kindKeyBuilder: (doc) => `NARC_MOVE:${String(doc.kind || 'MOVE')}`,
    placeField: 'placeId',
    attachmentFields: ['rxPhotoPath'],
    fieldAllowlist: [
      'id',
      'workspaceId',
      'itemId',
      'vaultAssetId',
      'kind',
      'qty',
      'balanceAfter',
      'byUid',
      'witnessUid',
      'rxPhotoPath',
      'note',
      'createdAt',
      'schedule',
      'narcSchedule',
      'patientLinked',
    ],
    isRestricted: (doc) =>
      Boolean(
        doc.patientLinked ||
          doc.schedule === 'X' ||
          doc.schedule === 'NDPS' ||
          doc.narcSchedule === 'X' ||
          doc.narcSchedule === 'NDPS' ||
          doc.rxPhotoPath,
      ),
    retentionClass: 'SCHEDULE_H1',
    anchorField: 'createdAt',
    isFinished: () => true,
    resolveAnchor: (doc) => (doc.createdAt ? parseTimestampOrDate(doc.createdAt) : null),
  },

  ops_cli_narc_counts: {
    collection: 'ops_cli_narc_counts',
    sectorField: 'sector',
    kindKeyBuilder: () => 'NARC_COUNT',
    placeField: 'placeId',
    attachmentFields: [],
    fieldAllowlist: [
      'id',
      'workspaceId',
      'vaultAssetId',
      'counterUid',
      'witnessUid',
      'counterTally',
      'witnessTally',
      'expected',
      'state',
      'requestId',
      'createdAt',
      'completedAt',
    ],
    isRestricted: () => false,
    retentionClass: 'NDPS_REGISTER',
    anchorField: 'createdAt',
    isFinished: (doc) => doc.state === 'AGREED' || doc.state === 'DISPUTED' || Boolean(doc.completedAt),
    resolveAnchor: (doc) =>
      doc.completedAt
        ? parseTimestampOrDate(doc.completedAt)
        : doc.createdAt
          ? parseTimestampOrDate(doc.createdAt)
          : null,
  },

  ops_cli_sample_bags: {
    collection: 'ops_cli_sample_bags',
    sectorField: 'sector',
    kindKeyBuilder: () => 'SAMPLE_BAG',
    placeField: 'placeId',
    attachmentFields: ['coldBoxPhotoPath'],
    fieldAllowlist: [
      'id',
      'workspaceId',
      'placeId',
      'barcodes',
      'receivedBarcodes',
      'stage',
      'stageAt',
      'coldBoxPhotoPath',
      'rejected',
      'rejectedItemKeys',
      'assignedRunnerUid',
      'homeCollectionJobId',
      'patientLinked',
      'createdAt',
      'createdBy',
      'updatedAt',
      'updatedBy',
      'closedAt',
      'closedBy',
    ],
    isRestricted: (doc) =>
      Boolean(
        doc.patientLinked ||
          (Array.isArray(doc.rejected) && doc.rejected.length > 0) ||
          (Array.isArray(doc.rejectedItemKeys) && doc.rejectedItemKeys.length > 0),
      ),
    retentionClass: 'OPERATIONAL_REGISTER',
    anchorField: 'closedAt',
    isFinished: (doc) => doc.stage === 'CLOSED' || Boolean(doc.closedAt),
    resolveAnchor: (doc) => {
      if (doc.closedAt) return parseTimestampOrDate(doc.closedAt)
      if (doc.createdAt) return parseTimestampOrDate(doc.createdAt)
      return null
    },
  },

  ops_cli_sample_handovers: {
    collection: 'ops_cli_sample_handovers',
    sectorField: 'sector',
    kindKeyBuilder: () => 'SAMPLE_HANDOVER',
    attachmentFields: [],
    fieldAllowlist: [
      'id',
      'workspaceId',
      'bagId',
      'expiresAtMs',
      'status',
      'createdByUid',
      'createdAtMs',
      'confirmedAtMs',
    ],
    isRestricted: () => false,
    retentionClass: 'OPERATIONAL_REGISTER',
    anchorField: 'createdAtMs',
    isFinished: (doc) => doc.status === 'CONFIRMED' || doc.status === 'EXPIRED' || Boolean(doc.confirmedAtMs),
    resolveAnchor: (doc) =>
      doc.confirmedAtMs
        ? parseTimestampOrDate(doc.confirmedAtMs)
        : doc.createdAtMs
          ? parseTimestampOrDate(doc.createdAtMs)
          : null,
  },

  ops_cli_job_orders: {
    collection: 'ops_cli_job_orders',
    sectorField: 'sector',
    kindKeyBuilder: (doc) => `JOB_ORDER:${String(doc.kind || 'ORDER')}`,
    placeField: 'placeId',
    attachmentFields: ['sentPhotoPath', 'returnedPhotoPath'],
    fieldAllowlist: [
      'id',
      'workspaceId',
      'kind',
      'caseToken',
      'patientToken',
      'patientLinked',
      'placeId',
      'vendorName',
      'vendorPartyId',
      'stage',
      'remakeCount',
      'dueAt',
      'expectedReturnAt',
      'sentPhotoPath',
      'returnedPhotoPath',
      'overdueRequestRaised',
      'stages',
      'createdAt',
      'createdBy',
      'updatedAt',
      'updatedBy',
    ],
    isRestricted: (doc) => Boolean(doc.patientToken || doc.patientLinked),
    retentionClass: 'OPERATIONAL_REGISTER',
    anchorField: 'createdAt',
    isFinished: (doc) => doc.stage === 'FITTED' || Boolean(doc.fittedAt),
    resolveAnchor: (doc) => (doc.createdAt ? parseTimestampOrDate(doc.createdAt) : null),
  },

  ops_cli_dialyzers: {
    collection: 'ops_cli_dialyzers',
    sectorField: 'sector',
    kindKeyBuilder: () => 'DIALYZER',
    placeField: 'placeId',
    attachmentFields: [],
    fieldAllowlist: [
      'id',
      'workspaceId',
      'dialyzerToken',
      'patientToken',
      'patientLinked',
      'placeId',
      'maxReuses',
      'uses',
      'useCount',
      'state',
      'status',
      'discardReason',
      'discardedAt',
      'discardedBy',
      'lastUseAt',
      'lastUsedAt',
      'rinsePassed',
      'germicidePresent',
      'residualTested',
      'createdAt',
      'createdBy',
      'updatedAt',
      'updatedBy',
    ],
    isRestricted: (doc) => Boolean(doc.patientToken || doc.patientLinked),
    retentionClass: 'OPERATIONAL_REGISTER',
    anchorField: 'createdAt',
    isFinished: (doc) => doc.state === 'DISCARDED' || Boolean(doc.discardedAt),
    resolveAnchor: (doc) =>
      doc.discardedAt
        ? parseTimestampOrDate(doc.discardedAt)
        : doc.createdAt
          ? parseTimestampOrDate(doc.createdAt)
          : null,
  },

  ops_cli_tele_sessions: {
    collection: 'ops_cli_tele_sessions',
    sectorField: 'sector',
    kindKeyBuilder: () => 'TELE_SESSION',
    placeField: 'placeId',
    attachmentFields: [],
    fieldAllowlist: [
      'id',
      'workspaceId',
      'bookingId',
      'doctorUid',
      'patientToken',
      'patientLinked',
      'phoneMasked',
      'placeId',
      'scheduledAt',
      'consentState',
      'consentTextVersion',
      'consentAt',
      'expiresAt',
      'startedAt',
      'endedAt',
      'createdAt',
      'createdBy',
      'updatedAt',
      'updatedBy',
    ],
    isRestricted: (doc) => Boolean(doc.patientToken || doc.patientLinked || doc.phoneMasked),
    retentionClass: 'OPERATIONAL_REGISTER',
    anchorField: 'createdAt',
    isFinished: (doc) =>
      doc.consentState === 'EXPIRED' || doc.consentState === 'REVOKED' || Boolean(doc.endedAt),
    resolveAnchor: (doc) =>
      doc.endedAt
        ? parseTimestampOrDate(doc.endedAt)
        : doc.createdAt
          ? parseTimestampOrDate(doc.createdAt)
          : null,
  },

  ops_cli_ivf_witness: {
    collection: 'ops_cli_ivf_witness',
    sectorField: 'sector',
    kindKeyBuilder: (doc) => `IVF_WITNESS:${String(doc.stepKind || doc.step || 'STEP')}`,
    attachmentFields: [],
    fieldAllowlist: [
      'id',
      'workspaceId',
      'caseToken',
      'patientLinked',
      'stepKind',
      'step',
      'operatorUid',
      'witnessUid',
      'status',
      'witnessCaseToken',
      'confirmedAt',
      'mismatchRequestId',
      'createdAt',
      'createdBy',
      'updatedAt',
      'updatedBy',
    ],
    isRestricted: (doc) => Boolean(doc.caseToken || doc.patientLinked),
    retentionClass: 'OPERATIONAL_REGISTER',
    anchorField: 'createdAt',
    isFinished: (doc) => doc.status === 'CONFIRMED' || doc.status === 'MISMATCH' || Boolean(doc.confirmedAt),
    resolveAnchor: (doc) =>
      doc.confirmedAt
        ? parseTimestampOrDate(doc.confirmedAt)
        : doc.createdAt
          ? parseTimestampOrDate(doc.createdAt)
          : null,
  },
}

export const OPS_EVIDENCE_EXCLUSIONS: Record<string, string> = {
  ops_cli_sample_settings: 'Workspace sample tracking stage limits and runner configuration (SO-P262, settings, not evidence)',
  ops_cli_narc_items: 'Narcotics vault item catalog and inventory master records (SO-P267, movements tracked in ops_cli_narc_moves)',
  ops_bed_states: 'Operational ward bed occupancy and readiness state (SO-P202, ephemeral room state sibling)',
  ops_access_jobs: 'Ephemeral batch access role update background jobs (SO-P8, background job processing, not evidence)',
  ops_places: 'Physical and functional place hierarchy, not an immutable operational evidence log',
  ops_place_tokens: 'Ephemeral security tokens for place QR scanning',
  ops_operations: 'Idempotency operation replay records',
  ops_parties: 'Directory of external parties and contractors',
  ops_workers: 'Worker roster profiles and contact directory',
  ops_party_links: 'Internal user to party link mappings',
  ops_sector_roles: 'User sector role assignment configuration',
  ops_templates: 'Form template definitions and version drafts',
  ops_duty_roles: 'Duty role configuration catalog',
  ops_departments: 'Workspace department catalog for duty role organization',
  ops_shifts: 'Shift timing and staffing configuration',
  ops_coverage_rules: 'Shift coverage and staffing requirement rules',
  ops_roster_changes: 'Internal change tracking events for rosters',
  ops_schedules: 'Recurring schedule definitions',
  ops_due: 'Ephemeral operational window due status items',
  ops_request_kinds: 'Request kind definitions catalog',
  ops_approval_settings: 'Workspace-level outside approval configuration',
  ops_import_jobs: 'Ephemeral batch import jobs',
  ops_wage_rates: 'Internal wage rate tables with financial privacy constraints',
  ops_engagements: 'Delivery engagement metadata covered by delivery financial boundary (SO-P41)',
  ops_engagement_finance: 'Internal billing and margin finance data restricted by delivery boundary (SO-P41)',
  ops_allocations: 'Internal staff allocation records restricted by delivery boundary (SO-P41)',
  ops_margin_rollups: 'Internal financial margin rollups restricted by delivery boundary (SO-P41)',
  ops_exports: 'Export job records and download metadata',
  ops_pms_events: 'Server-only Stays PMS inbound event log; public edge reduction drops all guest data (SO-D207, SO-P201)',
  ops_pms_outbox: 'Server-only Stays PMS outbox write-back queue (SO-P201)',
  ops_pms_snapshots: 'Server-only Stays PMS poller room state snapshot for diffing (SO-P126, SO-D207)',
  ops_connectors: 'Workspace sector connector integration configuration (SO-P275, config, not evidence)',
  ops_connector_events: 'Server-only connector event operational log with 90 d TTL (SO-P275, reduced operational log, 90 d TTL)',
  ops_retention_settings: 'Workspace retention policy configuration',
  ops_privacy_settings: 'Workspace-level privacy and offline expiry configuration (SO-P9, SO-P10)',
  ops_phone_access: 'Workspace phone access configuration (SO-D298, access configuration, not evidence)',
  ops_phone_access_people: 'Person-level phone access overrides (SO-D298, access configuration, not evidence)',
  ops_legal_holds: 'Active and historical legal hold policies',
  ops_retention_bin: 'Retention bin storing docs awaiting purge',
  ops_retention_notices: 'Daily retention notice digests',
  ops_retention_log: 'Immutable audit trail of purged records',
  ops_customers: 'Customer directory and consent records, managed via customer privacy lifecycle',
  ops_customer_contacts: 'Customer phone and email contact details protected by sector access boundaries',
  ops_customer_phones: 'Workspace phone HMAC records enforcing uniqueness',
  ops_customer_external_ids: 'Workspace customer external ID hash index enforcing uniqueness (SO-P277)',
  ops_booking_plans: 'Booking plan and membership tier catalog definitions',
  ops_bookables: 'Bookable resources and scheduling patterns catalog',
  ops_booking_sessions: 'Ephemeral session capacity counters and booking references',
  ops_bookings: 'Operational booking entries and waitlist queue states',
  ops_memberships: 'Customer membership contracts and usage tracking',
  ops_booking_dues: 'Booking fee due tracking records',
  ops_lf_items: 'Lost & found physical item custody and disposal lifecycle (SO-D294)',
  ops_lf_claims: 'Lost & found claims and masked claimant metadata lifecycle (SO-D207, SO-D295)',
  ops_lf_settings: 'Lost & found property hold duration settings (SO-D294)',
  ops_guest_status: 'Ephemeral guest request status tokens, purged 24h after request closure (SO-P119)',
  ops_guest_room_codes: 'Server-only Stays room codes for WhatsApp guest requests (SO-P120)',
  ops_guest_wa_sessions: 'Ephemeral WhatsApp guest request sessions, purged after expiry (SO-P120)',
  ops_guest_wa_contacts: 'Server-only encrypted guest phone contacts, purged on request close (SO-P120, SO-D299)',
  ops_owner_feeds: 'Homestay and villa owner feed settings and masked owner metadata (SO-P121)',
  ops_owner_feed_pii: 'Server-only Stays owner phone and email PII (SO-P121)',
  ops_owner_feed_secrets: 'Server-only owner feed link hashes and OTP verification secrets (SO-P121)',
  ops_owner_digests: 'Server-only frozen daily owner proof digests (SO-P121)',
  ops_change_requests: 'Delivery CR / UAT ledger: decisions are in ops_approvals (A10 evidence); ledger export comes with E5/E6',
  ops_uat_checklists: 'Delivery CR / UAT ledger: decisions are in ops_approvals (A10 evidence); ledger export comes with E5/E6',
  ops_uat_rounds: 'Delivery CR / UAT ledger: decisions are in ops_approvals (A10 evidence); ledger export comes with E5/E6',
  ops_engagement_contacts: 'admin-only money / client phones, never exported',
  ops_change_request_finance: 'admin-only money / client phones, never exported',
}

export interface RetentionStarterClass {
  keepDays: number
  minKeepDays?: number
  source: string
  checkYourState: boolean
  confirmed: boolean
}

export const OPS_RETENTION_STARTERS: Record<string, RetentionStarterClass> = {
  OPERATIONAL_REGISTER: {
    keepDays: 1095,
    source: 'Operational registers default (SO-P42)',
    checkYourState: true,
    confirmed: true,
  },
  INCIDENT: {
    keepDays: 2555,
    source: 'Incidents statutory default (SO-P42)',
    checkYourState: true,
    confirmed: true,
  },
  QUALITY_HOLD: {
    keepDays: 2555,
    source: 'Quality holds statutory default (SO-P42)',
    checkYourState: true,
    confirmed: true,
  },
  WAGE_SHEET: {
    keepDays: 1095,
    source: 'Wage sheets statutory default (SO-P42)',
    checkYourState: true,
    confirmed: true,
  },
  CLIENT_APPROVAL: {
    keepDays: 2555,
    source: 'Client approvals statutory default (SO-P42)',
    checkYourState: true,
    confirmed: true,
  },
  CR_DECISION: {
    keepDays: 2555,
    source: 'CR decisions statutory default (SO-P42)',
    checkYourState: true,
    confirmed: true,
  },
  MEASUREMENT_PACK: {
    keepDays: 2555,
    source: 'check for your contract',
    checkYourState: true,
    confirmed: true,
  },
  BMW_REGISTER: {
    keepDays: 1825,
    minKeepDays: 1825,
    source: 'Bio-Medical Waste Management Rules 2016 Rule 14(3) (H§5)',
    checkYourState: true,
    confirmed: true,
  },
  OSH_REGISTER: {
    keepDays: 1825,
    minKeepDays: 1825,
    source: 'Occupational safety and health registers, OSH Code (C§5)',
    checkYourState: true,
    confirmed: true,
  },
  TENANT_GUEST_ID: {
    keepDays: 90,
    source: 'Tenant and guest identification records (SO-P73)',
    checkYourState: true,
    confirmed: true,
  },
  VISITOR_ID: {
    keepDays: 30,
    source: 'Visitor access identification logs (SO-P73)',
    checkYourState: true,
    confirmed: true,
  },
  BROKER_KYC: {
    keepDays: 1825,
    source: 'Broker KYC and compliance records (SO-P73)',
    checkYourState: true,
    confirmed: true,
  },
  // Six new clinic retention starter classes (R39 M16.a, SO-P274)
  PCPNDT_FORM_F: {
    keepDays: 730,
    minKeepDays: 730,
    source: 'PCPNDT Act 1994 s.29, records kept 2 years',
    checkYourState: true,
    confirmed: true,
  },
  SCHEDULE_H1: {
    keepDays: 1095,
    minKeepDays: 1095,
    source: 'Drugs Rules 1945 r.65, H1 register 3 years',
    checkYourState: true,
    confirmed: true,
  },
  SCHEDULE_X: {
    keepDays: 730,
    minKeepDays: 730,
    source: 'Drugs Rules r.65, X register 2 years',
    checkYourState: true,
    confirmed: true,
  },
  NDPS_REGISTER: {
    keepDays: 730,
    minKeepDays: 730,
    source: 'NDPS Rules 1985, 2 years',
    checkYourState: true,
    confirmed: true,
  },
  BLOOD_BANK: {
    keepDays: 1825,
    minKeepDays: 1825,
    source: 'Drugs Rules Schedule F Part XII-B, 5 years',
    checkYourState: true,
    confirmed: true,
  },
  AERB_DOSE: {
    keepDays: 1825,
    minKeepDays: 1825,
    source: 'unconfirmed',
    checkYourState: true,
    confirmed: false,
  },
}

/* Helpers */

export function isRestrictedEvidenceSourceItem(
  source: EvidenceSourceCollection,
  doc: Record<string, unknown>,
): boolean {
  const def = OPS_EVIDENCE_SOURCES[source]
  if (!def) return false
  return def.isRestricted(doc)
}

export function getEvidenceKindKey(
  source: EvidenceSourceCollection,
  doc: Record<string, unknown>,
): string {
  const def = OPS_EVIDENCE_SOURCES[source]
  if (!def) return 'UNKNOWN'
  return def.kindKeyBuilder(doc)
}

export function buildTemplateEvidenceKindKey(template: { kind?: string }): string {
  return getEvidenceKindKey('ops_records', { kind: template.kind, templateKind: template.kind })
}

export function buildRequestEvidenceKindKey(kindKey: string): string {
  return getEvidenceKindKey('ops_requests', { kindKey })
}

export function sanitizeEvidenceFields(
  source: EvidenceSourceCollection,
  doc: Record<string, unknown>,
): Record<string, unknown> {
  const def = OPS_EVIDENCE_SOURCES[source]
  if (!def) return {}
  const result: Record<string, unknown> = {}
  for (const field of def.fieldAllowlist) {
    if (doc[field] !== undefined) {
      result[field] = doc[field]
    }
  }
  return result
}

export function isNearMissRequestDoc(
  doc: Record<string, unknown> | null | undefined,
): boolean {
  if (!doc || typeof doc !== 'object') return false
  const kindSnapshotKey =
    typeof doc.kindSnapshot === 'object' && doc.kindSnapshot !== null
      ? (doc.kindSnapshot as { key?: unknown }).key
      : undefined
  return (
    doc.kind === 'NEAR_MISS' ||
    doc.kindKey === 'NEAR_MISS' ||
    kindSnapshotKey === 'NEAR_MISS'
  )
}

export function sanitizeNearMissForExport(
  doc: Record<string, unknown>,
  nearMissExportPeople: boolean,
): Record<string, unknown> {
  const isNearMiss = isNearMissRequestDoc(doc)
  if (!isNearMiss || nearMissExportPeople) {
    return doc
  }
  const clean = { ...doc }
  // leave out raiser / assignee / done-by / verifier uids and names
  delete clean.raisedBy
  delete clean.raisedByUid
  delete clean.raisedByName
  delete clean.createdBy
  delete clean.assignedTo
  delete clean.assignedToName
  delete clean.assigneeUid
  delete clean.doneBy
  delete clean.doneByUid
  delete clean.closedBy
  delete clean.verifiedBy
  delete clean.verifiedByUid
  // leave out every free-text field
  delete clean.title
  delete clean.description
  delete clean.details
  delete clean.notes
  delete clean.whatNearlyHappened
  delete clean.immediateAction
  if (clean.fields && typeof clean.fields === 'object') {
    const cleanFields = { ...(clean.fields as Record<string, unknown>) }
    delete cleanFields.whatNearlyHappened
    delete cleanFields.immediateAction
    clean.fields = cleanFields
  }
  return clean
}

export function sanitizeNearMissEventForExport(
  event: Record<string, unknown>,
  nearMissExportPeople: boolean,
): Record<string, unknown> {
  if (nearMissExportPeople) return event
  const clean = { ...event }
  delete clean.byUid
  delete clean.byName
  delete clean.reason
  delete clean.note
  return clean
}

/* Input shapes & validators */

export interface EvidenceExportScope {
  sectors: OpsSector[]
  placeIds?: string[]
  sources?: EvidenceSourceCollection[]
  kindKeys?: string[]
  from: string
  to: string
  includeRestricted?: string[]
}

export interface RequestEvidenceExportInput {
  workspaceId: string
  scope: EvidenceExportScope
  opId: string
}

export interface GetEvidenceExportDownloadInput {
  exportId: string
}

export interface RetentionClassConfig {
  keepDays: number
  reason?: string
}

export interface SetRetentionSettingsInput {
  workspaceId: string
  classes: Record<string, RetentionClassConfig>
  opId: string
}

export type LegalHoldTargetType = 'PLACE' | 'KIND' | 'RECORD'

export interface LegalHoldTarget {
  type: LegalHoldTargetType
  placeId?: string
  kindKey?: string
  source?: EvidenceSourceCollection
  recordId?: string
}

export interface SetLegalHoldInput {
  workspaceId: string
  target: LegalHoldTarget
  reason: string
  opId: string
}

export interface RequestLegalHoldLiftInput {
  workspaceId: string
  holdId: string
  reason: string
  opId: string
}

export interface ConfirmLegalHoldLiftInput {
  workspaceId: string
  holdId: string
  opId: string
}

export interface CancelLegalHoldLiftInput {
  workspaceId: string
  holdId: string
  opId: string
}

export interface RestoreFromRetentionBinInput {
  workspaceId: string
  binId: string
  opId: string
}

const MAX_EXPORT_TIMESPAN_MS = 3 * 366 * 24 * 60 * 60 * 1000 // 3 years

export function validateRequestEvidenceExportInput(raw: unknown): RequestEvidenceExportInput {
  if (!raw || typeof raw !== 'object') {
    throw new Error('Input must be an object.')
  }
  const input = raw as Partial<RequestEvidenceExportInput>
  if (!input.workspaceId || typeof input.workspaceId !== 'string' || !input.workspaceId.trim()) {
    throw new Error('workspaceId is required.')
  }
  if (!input.opId || typeof input.opId !== 'string' || input.opId.trim().length < 1 || input.opId.trim().length > 120) {
    throw new Error('opId must be between 1 and 120 characters.')
  }
  if (!input.scope || typeof input.scope !== 'object') {
    throw new Error('scope is required.')
  }

  const { sectors, placeIds, sources, kindKeys, from, to, includeRestricted } = input.scope
  if (!Array.isArray(sectors) || sectors.length === 0) {
    throw new Error('scope.sectors must be a non-empty array.')
  }
  for (const s of sectors) {
    if (!OPS_SECTOR_KEYS.includes(s as OpsSector)) {
      throw new Error(`Invalid sector in scope.sectors: ${String(s)}`)
    }
  }

  if (sources !== undefined && sources !== null) {
    if (!Array.isArray(sources) || sources.some((s) => typeof s !== 'string')) {
      throw new Error('scope.sources must be an array of strings.')
    }
    const validSources = Object.keys(OPS_EVIDENCE_SOURCES) as EvidenceSourceCollection[]
    const seen = new Set<string>()
    for (const s of sources) {
      if (!validSources.includes(s as EvidenceSourceCollection)) {
        throw new Error(`Invalid source in scope.sources: ${String(s)}`)
      }
      if (seen.has(s)) {
        throw new Error(`Duplicate source in scope.sources: ${String(s)}`)
      }
      seen.add(s)
    }
  }

  if (typeof from !== 'string' || !from.trim()) {
    throw new Error('scope.from is required.')
  }
  if (typeof to !== 'string' || !to.trim()) {
    throw new Error('scope.to is required.')
  }

  const fromDate = new Date(from)
  const toDate = new Date(to)
  if (Number.isNaN(fromDate.getTime())) {
    throw new Error('scope.from must be a valid date.')
  }
  if (Number.isNaN(toDate.getTime())) {
    throw new Error('scope.to must be a valid date.')
  }
  if (fromDate.getTime() > toDate.getTime()) {
    throw new Error('scope.from must not be after to.')
  }
  if (toDate.getTime() - fromDate.getTime() > MAX_EXPORT_TIMESPAN_MS) {
    throw new Error('Export period cannot exceed 3 years.')
  }

  if (placeIds !== undefined && placeIds !== null && (!Array.isArray(placeIds) || placeIds.some((p) => typeof p !== 'string'))) {
    throw new Error('scope.placeIds must be an array of strings.')
  }
  if (kindKeys !== undefined && kindKeys !== null && (!Array.isArray(kindKeys) || kindKeys.some((k) => typeof k !== 'string'))) {
    throw new Error('scope.kindKeys must be an array of strings.')
  }
  if (includeRestricted !== undefined && includeRestricted !== null && (!Array.isArray(includeRestricted) || includeRestricted.some((r) => typeof r !== 'string'))) {
    throw new Error('scope.includeRestricted must be an array of strings.')
  }

  return {
    workspaceId: input.workspaceId.trim(),
    scope: {
      sectors: sectors as OpsSector[],
      placeIds: placeIds && Array.isArray(placeIds) ? placeIds.map((p) => p.trim()) : undefined,
      sources: sources && Array.isArray(sources) && sources.length > 0 ? (sources as EvidenceSourceCollection[]) : undefined,
      kindKeys: kindKeys && Array.isArray(kindKeys) ? kindKeys.map((k) => k.trim()) : undefined,
      from: fromDate.toISOString(),
      to: toDate.toISOString(),
      includeRestricted: includeRestricted && Array.isArray(includeRestricted) ? includeRestricted.map((r) => r.trim()) : undefined,
    },
    opId: input.opId.trim(),
  }
}

export function validateGetEvidenceExportDownloadInput(raw: unknown): GetEvidenceExportDownloadInput {
  if (!raw || typeof raw !== 'object') {
    throw new Error('Input must be an object.')
  }
  const input = raw as Partial<GetEvidenceExportDownloadInput>
  if (!input.exportId || typeof input.exportId !== 'string' || !input.exportId.trim()) {
    throw new Error('exportId is required.')
  }
  return {
    exportId: input.exportId.trim(),
  }
}

export function validateSetRetentionSettingsInput(raw: unknown): SetRetentionSettingsInput {
  if (!raw || typeof raw !== 'object') {
    throw new Error('Input must be an object.')
  }
  const input = raw as Partial<SetRetentionSettingsInput>
  if (!input.workspaceId || typeof input.workspaceId !== 'string' || !input.workspaceId.trim()) {
    throw new Error('workspaceId is required.')
  }
  if (!input.opId || typeof input.opId !== 'string' || input.opId.trim().length < 1 || input.opId.trim().length > 120) {
    throw new Error('opId must be between 1 and 120 characters.')
  }
  if (!input.classes || typeof input.classes !== 'object' || Array.isArray(input.classes)) {
    throw new Error('classes must be an object map.')
  }

  const cleanedClasses: Record<string, RetentionClassConfig> = {}
  for (const [classKey, config] of Object.entries(input.classes)) {
    if (!config || typeof config !== 'object') {
      throw new Error(`Config for retention class ${classKey} must be an object.`)
    }
    const { keepDays, reason } = config as Partial<RetentionClassConfig>
    if (typeof keepDays !== 'number' || !Number.isInteger(keepDays) || keepDays < 30 || keepDays > 36500) {
      throw new Error(`keepDays must be between 30 and 36500 for class ${classKey}.`)
    }

    const starter = OPS_RETENTION_STARTERS[classKey]
    if (starter && keepDays < starter.keepDays) {
      if (!reason || typeof reason !== 'string' || reason.trim().length < 3 || reason.trim().length > 500) {
        throw new Error(`A justification reason is required (3-500 characters) when shortening class ${classKey} below starter default (${starter.keepDays} days).`)
      }
    }

    cleanedClasses[classKey] = {
      keepDays,
      reason: reason?.trim(),
    }
  }

  return {
    workspaceId: input.workspaceId.trim(),
    classes: cleanedClasses,
    opId: input.opId.trim(),
  }
}

export function validateSetLegalHoldInput(raw: unknown): SetLegalHoldInput {
  if (!raw || typeof raw !== 'object') {
    throw new Error('Input must be an object.')
  }
  const input = raw as Partial<SetLegalHoldInput>
  if (!input.workspaceId || typeof input.workspaceId !== 'string' || !input.workspaceId.trim()) {
    throw new Error('workspaceId is required.')
  }
  if (!input.opId || typeof input.opId !== 'string' || input.opId.trim().length < 1 || input.opId.trim().length > 120) {
    throw new Error('opId must be between 1 and 120 characters.')
  }
  if (!input.reason || typeof input.reason !== 'string' || input.reason.trim().length < 3 || input.reason.trim().length > 500) {
    throw new Error('reason must be between 3 and 500 characters.')
  }
  if (!input.target || typeof input.target !== 'object') {
    throw new Error('target is required.')
  }

  const { type, placeId, kindKey, source, recordId } = input.target
  if (type !== 'PLACE' && type !== 'KIND' && type !== 'RECORD') {
    throw new Error('target.type must be PLACE, KIND, or RECORD.')
  }

  const cleanedTarget: LegalHoldTarget = { type }

  if (type === 'PLACE') {
    if (!placeId || typeof placeId !== 'string' || !placeId.trim()) {
      throw new Error('target.placeId is required for PLACE hold.')
    }
    cleanedTarget.placeId = placeId.trim()
  } else if (type === 'KIND') {
    if (!kindKey || typeof kindKey !== 'string' || !kindKey.trim()) {
      throw new Error('target.kindKey is required for KIND hold.')
    }
    cleanedTarget.kindKey = kindKey.trim()
  } else if (type === 'RECORD') {
    if (!source || !Object.keys(OPS_EVIDENCE_SOURCES).includes(source as EvidenceSourceCollection)) {
      throw new Error(`target.source must be one of the evidence sources for RECORD hold. Got: ${String(source)}`)
    }
    if (!recordId || typeof recordId !== 'string' || !recordId.trim()) {
      throw new Error('target.recordId is required for RECORD hold.')
    }
    cleanedTarget.source = source as EvidenceSourceCollection
    cleanedTarget.recordId = recordId.trim()
  }

  return {
    workspaceId: input.workspaceId.trim(),
    target: cleanedTarget,
    reason: input.reason.trim(),
    opId: input.opId.trim(),
  }
}

export function validateRequestLegalHoldLiftInput(raw: unknown): RequestLegalHoldLiftInput {
  if (!raw || typeof raw !== 'object') {
    throw new Error('Input must be an object.')
  }
  const input = raw as Partial<RequestLegalHoldLiftInput>
  if (!input.workspaceId || typeof input.workspaceId !== 'string' || !input.workspaceId.trim()) {
    throw new Error('workspaceId is required.')
  }
  if (!input.holdId || typeof input.holdId !== 'string' || !input.holdId.trim()) {
    throw new Error('holdId is required.')
  }
  if (!input.reason || typeof input.reason !== 'string' || input.reason.trim().length < 3 || input.reason.trim().length > 500) {
    throw new Error('reason must be between 3 and 500 characters.')
  }
  if (!input.opId || typeof input.opId !== 'string' || input.opId.trim().length < 1 || input.opId.trim().length > 120) {
    throw new Error('opId must be between 1 and 120 characters.')
  }
  return {
    workspaceId: input.workspaceId.trim(),
    holdId: input.holdId.trim(),
    reason: input.reason.trim(),
    opId: input.opId.trim(),
  }
}

export function validateConfirmLegalHoldLiftInput(raw: unknown): ConfirmLegalHoldLiftInput {
  if (!raw || typeof raw !== 'object') {
    throw new Error('Input must be an object.')
  }
  const input = raw as Partial<ConfirmLegalHoldLiftInput>
  if (!input.workspaceId || typeof input.workspaceId !== 'string' || !input.workspaceId.trim()) {
    throw new Error('workspaceId is required.')
  }
  if (!input.holdId || typeof input.holdId !== 'string' || !input.holdId.trim()) {
    throw new Error('holdId is required.')
  }
  if (!input.opId || typeof input.opId !== 'string' || input.opId.trim().length < 1 || input.opId.trim().length > 120) {
    throw new Error('opId must be between 1 and 120 characters.')
  }
  return {
    workspaceId: input.workspaceId.trim(),
    holdId: input.holdId.trim(),
    opId: input.opId.trim(),
  }
}

export function validateCancelLegalHoldLiftInput(raw: unknown): CancelLegalHoldLiftInput {
  if (!raw || typeof raw !== 'object') {
    throw new Error('Input must be an object.')
  }
  const input = raw as Partial<CancelLegalHoldLiftInput>
  if (!input.workspaceId || typeof input.workspaceId !== 'string' || !input.workspaceId.trim()) {
    throw new Error('workspaceId is required.')
  }
  if (!input.holdId || typeof input.holdId !== 'string' || !input.holdId.trim()) {
    throw new Error('holdId is required.')
  }
  if (!input.opId || typeof input.opId !== 'string' || input.opId.trim().length < 1 || input.opId.trim().length > 120) {
    throw new Error('opId must be between 1 and 120 characters.')
  }
  return {
    workspaceId: input.workspaceId.trim(),
    holdId: input.holdId.trim(),
    opId: input.opId.trim(),
  }
}

export function validateRestoreFromRetentionBinInput(raw: unknown): RestoreFromRetentionBinInput {
  if (!raw || typeof raw !== 'object') {
    throw new Error('Input must be an object.')
  }
  const input = raw as Partial<RestoreFromRetentionBinInput>
  if (!input.workspaceId || typeof input.workspaceId !== 'string' || !input.workspaceId.trim()) {
    throw new Error('workspaceId is required.')
  }
  if (!input.binId || typeof input.binId !== 'string' || !input.binId.trim()) {
    throw new Error('binId is required.')
  }
  if (!input.opId || typeof input.opId !== 'string' || input.opId.trim().length < 1 || input.opId.trim().length > 120) {
    throw new Error('opId must be between 1 and 120 characters.')
  }
  return {
    workspaceId: input.workspaceId.trim(),
    binId: input.binId.trim(),
    opId: input.opId.trim(),
  }
}

/* R39 M16.a Retention locks (SO-P274, SO-D235, SO-D302, SO-P217) */

export function isTargetHeld(
  holds: Array<{ target: LegalHoldTarget; state?: string }> | undefined | null,
  source: string,
  record: Record<string, unknown>,
): boolean {
  if (!holds || !Array.isArray(holds)) return false
  for (const hold of holds) {
    if (hold.state && hold.state !== 'ACTIVE' && hold.state !== 'LIFT_PENDING') continue
    const { type, placeId, kindKey, source: holdSource, recordId } = hold.target || ({} as LegalHoldTarget)
    if (type === 'PLACE' && placeId && record.placeId === placeId) {
      return true
    }
    if (type === 'KIND' && kindKey) {
      const k = getEvidenceKindKey(source as EvidenceSourceCollection, record)
      if (
        k === kindKey ||
        record.kind === kindKey ||
        record.templateKind === kindKey ||
        `RECORD:${String(record.templateKind)}` === kindKey ||
        `REQUEST:${String(record.kindKey)}` === kindKey
      ) {
        return true
      }
    }
    if (type === 'RECORD' && holdSource === source && recordId === record.id) {
      return true
    }
  }
  return false
}

export interface RetentionLockResult {
  locked: boolean
  lockedUntil: Date | null
  classKey: string
  source: EvidenceSourceCollection
  confirmed: boolean
  held: boolean
  reason?: string
}

export function getRetentionLock(
  source: EvidenceSourceCollection,
  doc: Record<string, unknown>,
  settings?:
    | { classes?: Record<string, { keepDays?: number }> }
    | Record<string, { keepDays?: number }>
    | null,
  holds?: Array<{ target: LegalHoldTarget; state?: string }> | null,
  now: Date | number = new Date(),
): RetentionLockResult {
  const nowDate = typeof now === 'number' ? new Date(now) : now

  // 1. Resolve retention class
  const templateClass = typeof doc.retentionClass === 'string' ? doc.retentionClass : undefined
  const templateStarterKey =
    typeof doc.starterKey === 'string'
      ? doc.starterKey
      : ((doc.templateSnapshot as Record<string, unknown> | undefined)?.starterKey as string | undefined)

  const classKey = resolveItemRetentionClass(source, doc, templateClass, templateStarterKey)
  const starter = OPS_RETENTION_STARTERS[classKey]
  const confirmed = starter?.confirmed ?? true

  // 2. Resolve keepDays
  const rawClasses =
    settings && 'classes' in settings && settings.classes
      ? settings.classes
      : settings
  const classesMap = rawClasses as Record<string, { keepDays?: number }> | undefined | null
  const configuredKeepDays = classesMap?.[classKey]?.keepDays
  const keepDays = typeof configuredKeepDays === 'number' ? configuredKeepDays : (starter?.keepDays ?? 365)

  // 3. Resolve legal hold status
  const held = isTargetHeld(holds, source, doc)

  // 4. Resolve anchor
  const def = OPS_EVIDENCE_SOURCES[source]
  let anchorDate: Date | null = null
  if (def?.resolveAnchor) {
    anchorDate = def.resolveAnchor(doc)
  } else if (def?.anchorField && doc[def.anchorField]) {
    anchorDate = parseTimestampOrDate(doc[def.anchorField])
  }

  // If anchor is not resolved, fallback to timestamp fields on doc
  if (!anchorDate) {
    anchorDate = parseTimestampOrDate(
      doc.createdAt ||
        doc.createdAtMs ||
        doc.created_at ||
        doc.enteredAt ||
        doc.pickedAt ||
        doc.submittedAt ||
        doc.lockedAt ||
        doc.updatedAt ||
        doc.date,
    )
  }

  // 5. Calculate lockedUntil
  let lockedUntil: Date | null = null
  if (anchorDate) {
    lockedUntil = new Date(anchorDate.getTime() + keepDays * 86400 * 1000)
  }

  // 6. Determine locked
  let locked = false
  let reason: string | undefined

  if (held) {
    locked = true
    reason = 'HELD'
  } else if (lockedUntil && nowDate.getTime() < lockedUntil.getTime()) {
    locked = true
    reason = 'RETENTION_PERIOD'
  } else if (!anchorDate) {
    locked = true
    reason = 'UNRESOLVED_ANCHOR'
  }

  return {
    locked,
    lockedUntil,
    classKey,
    source,
    confirmed,
    held,
    ...(reason ? { reason } : {}),
  }
}

/* R39 M16.a Sealed archives & workspace delete (SO-P274) */

export interface SealedArchiveDoc {
  id: string
  workspaceId: string
  workspaceName: string
  formerOwnerUids: string[]
  createdAt: unknown
  countsByClass: Record<string, number>
  totalLockedCount: number
  nextPurgeAt: unknown | null
  state: 'SEALED' | 'EMPTIED'
}

export interface SealedArchiveItemDoc {
  id: string
  archiveId: string
  workspaceId: string
  workspaceName?: string
  source: EvidenceSourceCollection
  recordId: string
  classKey: string
  lockedUntil: unknown
  heldAtDeletion: boolean
  doc: Record<string, unknown>
  subcollections?: Record<string, Array<Record<string, unknown>>>
  attachmentPaths: string[]
  sha256: string
  createdAt: unknown
}

export interface RequestSealedArchiveExportInput {
  archiveId: string
  opId: string
}

export function validateRequestSealedArchiveExportInput(raw: unknown): RequestSealedArchiveExportInput {
  if (!raw || typeof raw !== 'object') {
    throw new Error('Input must be an object.')
  }
  const input = raw as Partial<RequestSealedArchiveExportInput>
  if (!input.archiveId || typeof input.archiveId !== 'string' || !input.archiveId.trim()) {
    throw new Error('archiveId is required.')
  }
  if (!input.opId || typeof input.opId !== 'string' || input.opId.trim().length < 1 || input.opId.trim().length > 120) {
    throw new Error('opId must be between 1 and 120 characters.')
  }
  return {
    archiveId: input.archiveId.trim(),
    opId: input.opId.trim(),
  }
}

export interface GetRetentionLockSummaryInput {
  workspaceId: string
}

export function validateGetRetentionLockSummaryInput(raw: unknown): GetRetentionLockSummaryInput {
  if (!raw || typeof raw !== 'object') {
    throw new Error('Input must be an object.')
  }
  const input = raw as Partial<GetRetentionLockSummaryInput>
  if (!input.workspaceId || typeof input.workspaceId !== 'string' || !input.workspaceId.trim()) {
    throw new Error('workspaceId is required.')
  }
  return {
    workspaceId: input.workspaceId.trim(),
  }
}

export interface RetentionLockSummaryResult {
  workspaceId: string
  countsByClass: Record<string, number>
  totalLocked: number
  nextUnlockDate: string | null
}


